import React, { useState } from 'react';
import {
  Modal,
  ModalOverlay,
  ModalBody,
  ModalContent,
  ModalHeader,
  ModalFooter,
  Image
} from '@chakra-ui/react';
import CloseModalIcon from '../../assets/close-modal-icon.svg';
import LeafIcon from '../../assets/leaf-icon.svg';
import Button from '../Button';
import { UnauthorizedDispatcher } from 'src/utils/unauthorized-dispatcher';

function AwardWriterModal(props) {
  const {
    isOpen,
    handleCreatePoint,
    onClose,
    previousPoint,
    title,
    bgColorOverlay,
    alreadyGivePoint,
    isLoading,
    dailyLimits,
    contentType
  } = props;
  const [selectedPoints, setSelectedPoints] = useState(previousPoint || 1);
  const [hasUserSelected, setHasUserSelected] = useState(false);
  const userId = JSON.parse(localStorage.getItem('user'))?.id;

  const pointOptions = [
    { value: 1, label: '+1', description: 'Good point' },
    { value: 2, label: '+2', description: 'Really helpful' },
    { value: 3, label: '+3', description: 'Nailed it' },
    { value: 4, label: '+4', description: 'This is so good' },
    { value: 5, label: '+5', description: 'Incredible effort' }
  ];

  const getPointLevel = (value) => {
    if (value === 1) return 'Appreciated';
    if (value === 2) return 'Meaningful';
    if (value === 3) return 'Impactful';
    if (value === 4) return 'Inspiring';
    if (value === 5) return 'Exceptional';
    return 'Appreciated';
  };

  const handlePointSelection = (value) => {
    if (value !== selectedPoints) {
      setSelectedPoints(value);
      setHasUserSelected(true);
    }
  };

  const displayPoints = !hasUserSelected && previousPoint > 1 ? previousPoint : selectedPoints;

  // Check if user can award based on daily limits
  const canAwardPoints = dailyLimits ? dailyLimits.canAward : true;
  const maxDailyLimit = dailyLimits ? dailyLimits.maxDailyPerRecipient : 5;
  const remainingPoints = dailyLimits ? dailyLimits.remainingForRecipient : maxDailyLimit;
  const maxPointsForSelection = Math.min(displayPoints, remainingPoints);

  // Disable point selection buttons that exceed remaining daily limit or when already awarded
  const isPointDisabled = (pointValue) => {
    if (alreadyGivePoint) {
      return pointValue !== previousPoint; // Only allow the previously awarded amount
    }
    if (!dailyLimits) return false;
    return pointValue > dailyLimits.remainingForRecipient;
  };

  return (
    <Modal isOpen={isOpen} isCentered onClose={onClose}>
      <ModalOverlay backgroundColor={bgColorOverlay || 'rgba(0, 0, 0, 0.5)'} />
      <ModalContent px={'15px'} py={5} bgColor={'#FAFAFA'} rounded={'12px'} maxW="408px">
        <ModalHeader p={0} w={'100%'} m={0} maxWidth={'unset'}>
          <div className="flex w-full items-center justify-between pb-2 border-b border-earth-brown-50">
            <p className="text-lg font-[800]">Award Writer</p>
            <button className="w-10 h-10 flex items-center justify-center" onClick={onClose}>
              <Image className="cursor-pointer" src={CloseModalIcon} alt="Close" />
            </button>
          </div>
        </ModalHeader>
        <ModalBody py={6} px={0}>
          <div className="flex flex-col items-center gap-2">
            <div className="flex flex-col items-center gap-1">
              <Image className="w-[40px] h-[40px]" src={LeafIcon} alt="Twigs" />
              <p className="text-[24px] font-[800] text-growth-green-500">Give Twigs!</p>
            </div>
            <p className="text-lg font-[600]">{title}</p>
            {alreadyGivePoint && (
              <div className="text-center mb-2">
                <p className="text-sm text-growth-green-600 font-[600]">
                  ✓ You've already awarded {previousPoint} Twig{previousPoint === 1 ? '' : 's'} to
                  this {contentType}
                </p>
              </div>
            )}
            {!alreadyGivePoint && dailyLimits && (
              <div className="text-center mb-2">
                <p className="text-sm text-gray-600">
                  {remainingPoints > 0
                    ? `${remainingPoints} Twig${remainingPoints === 1 ? '' : 's'} remaining today for this user`
                    : `Daily limit reached for this user (${maxDailyLimit} Twigs max per day)`}
                </p>
              </div>
            )}
            <div
              className={`p-4 rounded-[8px] w-full ${alreadyGivePoint ? 'bg-growth-green-50 border border-growth-green-200' : 'bg-white'}`}
            >
              <div className="flex flex-col items-center">
                <p className="text-[20px] font-[800] text-growth-green-500">
                  +{displayPoints}
                  {alreadyGivePoint && displayPoints === previousPoint && (
                    <span className="ml-2 text-growth-green-600">✓</span>
                  )}
                </p>
                <p className="text-lg font-semibold text-growth-green-900 mt-1">
                  {getPointLevel(displayPoints)}
                </p>
                <p className="text-lg text-[#4B5563] text-center mt-1 px-1">
                  {pointOptions.find((opt) => opt.value === displayPoints)?.description}
                </p>

                <div className="w-full mt-4">
                  <div className="flex justify-between text-sm mb-1 text-growth-green-900">
                    <span>Basic</span>
                    <span>Outstanding</span>
                  </div>
                  <div className="h-2 bg-gray-200 rounded-full w-full overflow-hidden">
                    <div
                      className="h-2 rounded-full transition-all duration-300 ease-in-out"
                      style={{
                        background:
                          'linear-gradient(90deg, #C8E6C9 1.02%, #4CAF50 55.95%, #204A22 91.59%)',
                        width: `${(displayPoints / 5) * 100}%`
                      }}
                    ></div>
                  </div>
                </div>

                <div className="flex w-full mt-4 justify-between">
                  {pointOptions.map((option) => {
                    const disabled = isPointDisabled(option.value);
                    return (
                      <button
                        key={option.value}
                        onClick={() => !disabled && handlePointSelection(option.value)}
                        disabled={disabled}
                        className={`
                          w-14 h-14 rounded-full font-[800] text-sm md:text-lg flex items-center justify-center 
                          transform transition-all duration-300 ease-in-out
                          ${
                            disabled
                              ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
                              : (alreadyGivePoint && option.value === previousPoint) ||
                                  (!alreadyGivePoint && displayPoints === option.value)
                                ? 'text-white shadow-md bg-growth-green'
                                : 'bg-growth-green-50 text-gray-800 hover:shadow-sm'
                          }
                          ${!disabled && ((alreadyGivePoint && option.value === previousPoint) || (!alreadyGivePoint && displayPoints === option.value)) ? 'hover:-translate-y-1' : !disabled ? 'hover:-translate-y-0.5' : ''}
                          ${!disabled ? 'active:scale-95' : ''}
                        `}
                      >
                        {option.label}
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>
        </ModalBody>
        <ModalFooter w="100%" p="0">
          <Button
            onClick={() => {
              if (!userId) return UnauthorizedDispatcher();
              handleCreatePoint(displayPoints);
            }}
            className="w-full h-[42px] font-[900]"
            isLoading={isLoading}
            disabled={alreadyGivePoint || !canAwardPoints || isPointDisabled(displayPoints)}
          >
            {alreadyGivePoint
              ? 'Already Awarded'
              : !canAwardPoints
                ? 'Daily Limit Reached'
                : 'Award Writer'}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}

export default AwardWriterModal;
